import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGlobal } from '../Context/GlobalContext';
import { parseAuthError, parseNetworkError, ERROR_MESSAGES } from '../utils/errorHandling';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { apiUrl } = useGlobal();
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize state based on the token in localStorage
  const [token, setToken] = useState(localStorage.getItem('brandvox_token'));
  const [adminToken, setAdminToken] = useState(localStorage.getItem('admin_token'));
  const [isLoggedIn, setIsLoggedIn] = useState(!!token);
  const [isAdmin, setIsAdmin] = useState(!!adminToken);
  const [needsOnboarding, setNeedsOnboarding] = useState(false); // Initialize as false instead of null
  const [isTestAccountLoggedIn, setIsTestAccountLoggedIn] = useState(false);
  const [warning, setWarning] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState(null);

  // Refs to avoid dependency issues
  const isLoggedInRef = useRef(isLoggedIn);
  const isAdminRef = useRef(isAdmin);

  // Update refs when state changes
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  useEffect(() => {
    isAdminRef.current = isAdmin;
  }, [isAdmin]);

  // Rate limiting state
  const [lastApiCall, setLastApiCall] = useState(0);
  const [rateLimitBackoff, setRateLimitBackoff] = useState(0);
  const MIN_API_INTERVAL = 1000; // Minimum 1 second between API calls

  // Circuit breaker state
  const [failureCount, setFailureCount] = useState({});
  const [blockedOperations, setBlockedOperations] = useState(new Set());
  const FAILURE_THRESHOLD = 3; // Number of failures before blocking
  const CIRCUIT_BREAKER_TIMEOUT = 30000; // 30 seconds

  // Request timeout and abort controller state
  // eslint-disable-next-line no-unused-vars
  const [activeRequests, setActiveRequests] = useState(new Map());
  const REQUEST_TIMEOUT = 30000; // 30 seconds

  // Auth check cooldown state
  const [lastAuthCheck, setLastAuthCheck] = useState(0);
  const [lastAdminAuthCheck, setLastAdminAuthCheck] = useState(0);
  const AUTH_CHECK_COOLDOWN = 1000; // 1 second between auth checks

  // Debounce utility for API calls with circuit breaker
  const debounceApiCall = useCallback(async (apiFunction, operationName, ...args) => {
    const now = Date.now();

    // Check circuit breaker
    if (blockedOperations.has(operationName)) {
      const lastFailure = failureCount[operationName]?.lastFailure || 0;
      if (now - lastFailure < CIRCUIT_BREAKER_TIMEOUT) {
        throw new Error(`Operation ${operationName} is temporarily blocked due to repeated failures`);
      } else {
        // Reset circuit breaker after timeout
        setBlockedOperations(prev => {
          const next = new Set(prev);
          next.delete(operationName);
          return next;
        });
        setFailureCount(prev => ({ ...prev, [operationName]: { count: 0, lastFailure: 0 } }));
      }
    }

    const timeSinceLastCall = now - lastApiCall;
    const backoffTime = Math.max(rateLimitBackoff - now, 0);

    if (timeSinceLastCall < MIN_API_INTERVAL || backoffTime > 0) {
      const waitTime = Math.max(MIN_API_INTERVAL - timeSinceLastCall, backoffTime);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    setLastApiCall(Date.now());

    // Create abort controller for timeout
    const controller = new AbortController();
    const requestId = `${operationName}-${Date.now()}`;

    // Set timeout
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, REQUEST_TIMEOUT);

    // Track active request
    setActiveRequests(prev => new Map(prev).set(requestId, controller));

    try {
      const result = await apiFunction(controller.signal, ...args);
      setRateLimitBackoff(0); // Reset backoff on success

      // Reset failure count on success
      if (failureCount[operationName]?.count > 0) {
        setFailureCount(prev => ({ ...prev, [operationName]: { count: 0, lastFailure: 0 } }));
      }

      return result;
    } catch (error) {
      // Don't count aborted requests as failures
      if (error.name === 'AbortError') {
        throw new Error(`Request ${operationName} timed out`);
      }

      // Track failures for circuit breaker
      const currentFailures = (failureCount[operationName]?.count || 0) + 1;
      setFailureCount(prev => ({
        ...prev,
        [operationName]: { count: currentFailures, lastFailure: Date.now() }
      }));

      if (currentFailures >= FAILURE_THRESHOLD) {
        setBlockedOperations(prev => new Set(prev).add(operationName));
      }

      // Handle 429 rate limiting with exponential backoff
      if (error.message?.includes('429') || error.message?.toLowerCase().includes('too many requests')) {
        const backoffDelay = Math.min(30000, 5000 * Math.pow(2, Math.floor(rateLimitBackoff / 5000))); // Max 30 seconds
        setRateLimitBackoff(Date.now() + backoffDelay);
      }

      throw error;
    } finally {
      // Clean up
      clearTimeout(timeoutId);
      setActiveRequests(prev => {
        const next = new Map(prev);
        next.delete(requestId);
        return next;
      });
    }
  }, [lastApiCall, rateLimitBackoff, failureCount, blockedOperations]);

  const checkAuth = useCallback(async (forceCheck = false) => {
    try {
      const currentToken = localStorage.getItem('brandvox_token');
      if (!currentToken) {
        setIsLoading(false);
        setIsLoggedIn(false);
        setToken(null);
        setNeedsOnboarding(false);
        return false;
      }

      // Prevent excessive auth checks
      const now = Date.now();
      if (!forceCheck && now - lastAuthCheck < AUTH_CHECK_COOLDOWN) {
        return isLoggedInRef.current;
      }

      setLastAuthCheck(now);

      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/auth/protected-endpoint`, {
          signal,
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      }, 'checkAuth');

      if (response.ok) {
        const data = await response.json();
        setIsLoggedIn(true);
        setToken(currentToken);

        // Always update workspace context with fresh data from auth check
        // This ensures we don't use stale workspace data from previous sessions
        if (data.workspace_id) {
          localStorage.setItem('current_workspace_id', data.workspace_id);
          localStorage.setItem('current_user_role', data.role);
          localStorage.setItem('needs_subscription_check', 'false');
        } else {
          // Clear any stale workspace data if no workspace provided
          localStorage.removeItem('current_workspace_id');
          localStorage.removeItem('current_workspace_name');
          localStorage.removeItem('current_user_role');
          localStorage.setItem('needs_subscription_check', 'true');
        }

        localStorage.setItem('user_email', data.email);

        // Check if first_login exists and is true
        // But respect local onboarding completion flag to avoid cache race conditions
        const localOnboardingComplete = localStorage.getItem('onboarding_completed') === 'true';
        setNeedsOnboarding(!localOnboardingComplete && data.first_login === true);

        // Check for impersonation info
        const impersonationData = localStorage.getItem('impersonation_info');
        if (impersonationData) {
          try {
            setImpersonationInfo(JSON.parse(impersonationData));
          } catch (error) {
            localStorage.removeItem('impersonation_info');
            setImpersonationInfo(null);
          }
        } else {
          setImpersonationInfo(null);
        }

        return true;
      }

      // Auth check failed, clean up all auth and workspace data
      localStorage.removeItem('brandvox_token');
      localStorage.removeItem('user_email');
      localStorage.removeItem('current_workspace_id');
      localStorage.removeItem('current_workspace_name');
      localStorage.removeItem('current_user_role');
      setToken(null);
      setIsLoggedIn(false);
      setNeedsOnboarding(false); // Reset onboarding status on failure
      return false;
    } catch (error) {
      localStorage.removeItem('brandvox_token');
      localStorage.removeItem('user_email');
      localStorage.removeItem('current_workspace_id');
      localStorage.removeItem('current_workspace_name');
      localStorage.removeItem('current_user_role');
      setToken(null);
      setIsLoggedIn(false);
      setNeedsOnboarding(false); // Reset onboarding status on error
      return false;
    } finally {
      setIsLoading(false); // Ensure loading state is turned off
    }
  }, [apiUrl, lastAuthCheck, debounceApiCall]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('brandvox_token');
    const workspaceId = params.get('workspace_id');
    const role = params.get('role');
    const email = params.get('email');
    const googleError = params.get('error');

    // Handle token from URL (from OAuth redirects or legacy)
    if (urlToken) {
      localStorage.setItem('brandvox_token', urlToken);
      setToken(urlToken);
      setIsLoggedIn(true);
      if (workspaceId) {
        localStorage.setItem('current_workspace_id', workspaceId);
      }
      if (role) {
        localStorage.setItem('current_user_role', role);
      }
      if (email) {
        localStorage.setItem('user_email', email);
      }
      loginWithToken(urlToken, false, workspaceId, role, email);

      params.delete('brandvox_token');
      params.delete('workspace_id');
      params.delete('role');
      params.delete('email');
      navigate({ search: params.toString() }, { replace: true });
    }

    // Handle OAuth errors from backend redirects
    if (googleError) {
      params.delete('error');
      navigate({ search: params.toString() }, {
        replace: true,
        state: { error: googleError }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate]);

  const checkAdminAuth = useCallback(async (forceCheck = false) => {
    try {
      const currentAdminToken = localStorage.getItem('admin_token');
      if (!currentAdminToken) {
        setIsAdmin(false);
        setAdminToken(null);
        return false;
      }

      // Prevent excessive admin auth checks
      const now = Date.now();
      if (!forceCheck && now - lastAdminAuthCheck < AUTH_CHECK_COOLDOWN) {
        return isAdminRef.current;
      }

      setLastAdminAuthCheck(now);

      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/admin/protected-endpoint`, {
          signal,
          headers: { Authorization: `Bearer ${currentAdminToken}` },
        });
      }, 'checkAdminAuth');

      if (response.ok) {
        setIsAdmin(true);
        setAdminToken(currentAdminToken);
        // Admin token is valid, but don't schedule recursive checks
        return true;
      }

      // Admin auth check failed, clean up
      localStorage.removeItem('admin_token');
      setAdminToken(null);
      setIsAdmin(false);
      return false;
    } catch (error) {
      localStorage.removeItem('admin_token');
      setAdminToken(null);
      setIsAdmin(false);
      return false;
    }
  }, [apiUrl, lastAdminAuthCheck, debounceApiCall]);

  const isTestAccount = useCallback(async () => {
    try {
      const token = localStorage.getItem('brandvox_token_test_account');
      if (!token) return false;

      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/auth/protected-endpoint-test-account`, {
          signal,
          headers: { Authorization: `Bearer ${token}` },
        });
      }, 'isTestAccount');

      if (response.ok) {
        setIsTestAccountLoggedIn(true);
        return true;
      }

      localStorage.removeItem('brandvox_token_test_account');
      setIsTestAccountLoggedIn(false);
      return false;
    } catch (error) {
      localStorage.removeItem('brandvox_token_test_account');
      setIsTestAccountLoggedIn(false);
      return false;
    }
  }, [apiUrl, debounceApiCall]);

  useEffect(() => {
    const runCheck = async () => {
      setIsLoading(true);
      // Run all auth checks in parallel for faster initial load
      await Promise.allSettled([
        checkAuth(true), // Force check on initial mount
        checkAdminAuth(),
        isTestAccount()
      ]);
      setIsLoading(false);
    };
    runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // login: Handles the actual login API call, stores token, sets logged-in and onboarding state.
  const login = useCallback(async (email, password) => {
    try {
      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/auth/login`, {
          signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
      }, 'login');

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = parseAuthError(response, result, ERROR_MESSAGES.login);
        throw new Error(errorMessage);
      }

      localStorage.setItem('brandvox_token', result.access_token);
      setToken(result.access_token);
      localStorage.setItem('user_email', email); // Store email if needed elsewhere

      // Store workspace context if provided
      if (result.workspace_id) {
        localStorage.setItem('current_workspace_id', result.workspace_id);
        localStorage.setItem('needs_subscription_check', 'false');
      } else {
        localStorage.setItem('needs_subscription_check', 'true');
      }
      if (result.role) {
        localStorage.setItem('current_user_role', result.role);
      }
      if (result.email) {
        localStorage.setItem('user_email', result.email);
      }
      setIsLoggedIn(true);

      setNeedsOnboarding(result.first_login === true); // Set onboarding state based on response

      // Check for pending invitation
      const pendingInvitation = localStorage.getItem('pending_invitation');
      if (pendingInvitation) {
        // Will be handled by JoinWorkspace component
        return { success: true, needsOnboarding: false, pendingInvitation };
      }

      return { success: true, needsOnboarding: result.first_login === true };

    } catch (error) {
      localStorage.removeItem('brandvox_token');
      localStorage.removeItem('user_email');
      localStorage.removeItem('current_workspace_id');
      localStorage.removeItem('current_workspace_name');
      localStorage.removeItem('current_user_role');
      setToken(null);
      setIsLoggedIn(false);
      setNeedsOnboarding(null);
      const errorMessage = parseNetworkError(error);
      return { success: false, error: errorMessage };
    }
  }, [apiUrl, debounceApiCall]);

  // loginWithToken: Handles login using a provided access token (e.g., from email verification or OAuth)
  const loginWithToken = useCallback(async (accessToken, needsOnboardingParam, workspaceId, role, email) => {
    try {
      // Verify the token is valid by making a request to protected endpoint
      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/auth/protected-endpoint`, {
          signal,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      }, 'loginWithToken');

      if (!response.ok) {
        throw new Error('Invalid token');
      }

      // Parse response to get first_login from server
      const data = await response.json();

      // Store token and set auth state
      localStorage.setItem('brandvox_token', accessToken);
      setToken(accessToken);
      // Store workspace context if provided (prefer URL params, fall back to server response)
      if (workspaceId || data.workspace_id) {
        localStorage.setItem('current_workspace_id', workspaceId || data.workspace_id);
        localStorage.setItem('needs_subscription_check', 'false');
      } else {
        localStorage.setItem('needs_subscription_check', 'true');
      }
      if (role || data.role) {
        localStorage.setItem('current_user_role', role || data.role);
      }
      if (email || data.email) {
        localStorage.setItem('user_email', email || data.email);
      }

      setIsLoggedIn(true);

      // Use first_login from server response, respecting local onboarding completion flag
      // This ensures Google OAuth users are correctly directed to onboarding
      const localOnboardingComplete = localStorage.getItem('onboarding_completed') === 'true';
      const serverNeedsOnboarding = !localOnboardingComplete && data.first_login === true;
      // Use parameter if explicitly true, otherwise use server response
      const finalNeedsOnboarding = needsOnboardingParam === true || serverNeedsOnboarding;
      setNeedsOnboarding(finalNeedsOnboarding);

      return { success: true, needsOnboarding: finalNeedsOnboarding };

    } catch (error) {
      localStorage.removeItem('brandvox_token');
      localStorage.removeItem('user_email');
      localStorage.removeItem('current_workspace_id');
      localStorage.removeItem('current_workspace_name');
      localStorage.removeItem('current_user_role');
      setToken(null);
      setIsLoggedIn(false);
      setNeedsOnboarding(false);
      const errorMessage = parseNetworkError(error);
      return { success: false, error: errorMessage };
    }
  }, [apiUrl, debounceApiCall]);

  // Admin-specific login
  const loginAsAdmin = useCallback(async (email, password) => {
    try {
      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/admin/login`, {
          signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ email, password }),
        });
      }, 'loginAsAdmin');

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Admin login failed');
      }

      localStorage.setItem('admin_token', result.access_token);
      setAdminToken(result.access_token);
      localStorage.setItem('admin_email', email);
      setIsAdmin(true);

      // Admin login successful, token is valid

      return { success: true };
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_email');
      setAdminToken(null);
      setIsAdmin(false);
      return { success: false, error: error.message };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, checkAdminAuth]);

  // Admin login with Google
  const loginAsAdminWithGoogle = useCallback(async (adminToken) => {
    try {
      // Verify the token is valid
      const response = await debounceApiCall(async (signal) => {
        return fetch(`${apiUrl}/api/v1/admin/protected-endpoint`, {
          signal,
          headers: { Authorization: `Bearer ${adminToken}` },
        });
      }, 'loginAsAdminWithGoogle');

      if (!response.ok) {
        throw new Error('Invalid admin token');
      }

      localStorage.setItem('admin_token', adminToken);
      setAdminToken(adminToken);
      setIsAdmin(true);

      // Admin login successful, token is valid

      return { success: true };
    } catch (error) {
      localStorage.removeItem('admin_token');
      setAdminToken(null);
      setIsAdmin(false);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, checkAdminAuth]);

  // exitImpersonation: Clear impersonation and return to admin
  const exitImpersonation = useCallback(() => {
    localStorage.removeItem('brandvox_token');
    localStorage.removeItem('impersonation_info');
    setToken(null);
    setIsLoggedIn(false);
    setImpersonationInfo(null);

    // Navigate back to admin
    navigate('/admin', { replace: true });
  }, [navigate]);

  // logout: Clears auth state and tokens.
  const logout = useCallback(() => {
    // Clear all auth-related tokens and data
    localStorage.removeItem('brandvox_token');
    localStorage.removeItem('user_email');

    // Clear test account tokens and workspace context
    localStorage.removeItem('impersonation_info');
    setToken(null);
    setImpersonationInfo(null);
    // Also clear test account if necessary
    localStorage.removeItem('brandvox_token_test_account');
    localStorage.removeItem('user_email_test_account');
    localStorage.removeItem('test_account_workspace_id');
    localStorage.removeItem('test_account_role');

    // Clear workspace context
    localStorage.removeItem('current_workspace_id');
    localStorage.removeItem('current_user_role');

    // Clear onboarding completion flag
    localStorage.removeItem('onboarding_completed');

    // Clear admin tokens
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');

    // Update state immediately
    setToken(null);
    setIsLoggedIn(false);
    setIsTestAccountLoggedIn(false);
    setAdminToken(null);
    setIsAdmin(false);
    setNeedsOnboarding(false);

    // Navigate to login after logout
    navigate('/login', { replace: true });
  }, [navigate]);

  // Get the current token - for API calls
  const getToken = useCallback(() => {
    return token || adminToken;
  }, [token, adminToken]);

  return (
    <AuthContext.Provider
      value={{
        token,
        adminToken,
        isLoggedIn,
        setIsLoggedIn,
        isAdmin,
        warning,
        setWarning,
        checkAuth,
        checkAdminAuth,
        login,
        loginWithToken,
        loginAsAdmin,
        loginAsAdminWithGoogle,
        logout,
        needsOnboarding,
        setNeedsOnboarding,
        isTestAccount,
        isLoading,
        isTestAccountLoggedIn,
        setIsTestAccountLoggedIn,
        getToken,
        impersonationInfo,
        exitImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
